/** Built-in org access roles and default feature matrix (mirrors API OrgAccessRoleDefaults). */

/** Invites are always for a person; scope is the access level they get, not the invitee. */
export function inviteScopeLabel(scope?: string | null): string {
  switch (scope) {
    case "Organization":
      return "Organization-wide access";
    case "Application":
      return "Application access";
    case "Team":
      return "Team access";
    default:
      return scope || "";
  }
}

export interface OrgRoleFeatureFlags {
  createContent: boolean;
  approveContent: boolean;
  manageOrganization: boolean;
  manageApplications: boolean;
  manageTeams: boolean;
  manageMembers: boolean;
  inviteMembers: boolean;
  accessOrgAdmin: boolean;
}

export interface OrgAccessRoleDefinition {
  key: string;
  displayName: string;
  description: string;
  enabled: boolean;
  required: boolean;
  features: OrgRoleFeatureFlags;
}

export interface OrgRoleFeatureMeta {
  key: keyof OrgRoleFeatureFlags;
  label: string;
  hint: string;
}

export const ORG_ROLE_FEATURE_META: OrgRoleFeatureMeta[] = [
  {
    key: "createContent",
    label: "Create content",
    hint: "Create and edit guides and guide packs",
  },
  {
    key: "approveContent",
    label: "Approve content",
    hint: "Review, approve, reject, and publish",
  },
  {
    key: "manageOrganization",
    label: "Manage organization",
    hint: "Org setup, branding, and terminology",
  },
  {
    key: "manageApplications",
    label: "Manage applications",
    hint: "Create and edit applications",
  },
  {
    key: "manageTeams",
    label: "Manage teams",
    hint: "Create and edit teams",
  },
  {
    key: "manageMembers",
    label: "Manage members",
    hint: "Discontinue members and reassign ownership",
  },
  {
    key: "inviteMembers",
    label: "Invite members",
    hint: "Send invites within allowed scope",
  },
  {
    key: "accessOrgAdmin",
    label: "Org admin console",
    hint: "Access the organization admin area",
  },
];

function flags(partial: Partial<OrgRoleFeatureFlags> = {}): OrgRoleFeatureFlags {
  return {
    createContent: false,
    approveContent: false,
    manageOrganization: false,
    manageApplications: false,
    manageTeams: false,
    manageMembers: false,
    inviteMembers: false,
    accessOrgAdmin: false,
    ...partial,
  };
}

export function createDefaultAccessRoles(): OrgAccessRoleDefinition[] {
  return [
    {
      key: "OrgAdmin",
      displayName: "Org Admin",
      description: "Full organization administration.",
      enabled: true,
      required: true,
      features: flags({
        createContent: true,
        approveContent: true,
        manageOrganization: true,
        manageApplications: true,
        manageTeams: true,
        manageMembers: true,
        inviteMembers: true,
        accessOrgAdmin: true,
      }),
    },
    {
      key: "AppAdmin",
      displayName: "App Admin",
      description: "Administer applications and their teams.",
      enabled: true,
      required: false,
      features: flags({
        createContent: true,
        approveContent: true,
        manageApplications: true,
        manageTeams: true,
        manageMembers: true,
        inviteMembers: true,
        accessOrgAdmin: true,
      }),
    },
    {
      key: "TeamAdmin",
      displayName: "Team Admin",
      description: "Administer a team and invite members.",
      enabled: true,
      required: false,
      features: flags({
        createContent: true,
        approveContent: true,
        manageTeams: true,
        inviteMembers: true,
        accessOrgAdmin: true,
      }),
    },
    {
      key: "Author",
      displayName: "Author",
      description: "Create and edit guides and packs.",
      enabled: true,
      required: false,
      features: flags({ createContent: true }),
    },
    {
      key: "Reviewer",
      displayName: "Reviewer",
      description: "Review content and create guides.",
      enabled: true,
      required: false,
      features: flags({ createContent: true, approveContent: true }),
    },
    {
      key: "Approver",
      displayName: "Approver",
      description: "Approve and publish content.",
      enabled: true,
      required: false,
      features: flags({ createContent: true, approveContent: true }),
    },
    {
      key: "Worker",
      displayName: "Worker",
      description: "End user — follow published guides.",
      enabled: true,
      required: true,
      features: flags(),
    },
  ];
}

/** Merge saved roles with defaults so all seven keys remain present. */
export function normalizeAccessRoles(
  input?: OrgAccessRoleDefinition[] | null
): OrgAccessRoleDefinition[] {
  const defaults = createDefaultAccessRoles();
  if (!input?.length) return defaults;

  const byKey = new Map<string, OrgAccessRoleDefinition>();
  for (const role of input) {
    if (role?.key) byKey.set(role.key.trim().toLowerCase(), role);
  }

  return defaults.map((role) => {
    const saved = byKey.get(role.key.toLowerCase());
    if (!saved) return { ...role, features: { ...role.features } };
    return {
      ...role,
      displayName: (saved.displayName || role.displayName).trim(),
      description: (saved.description || role.description).trim(),
      enabled: role.required ? true : !!saved.enabled,
      features: { ...role.features, ...(saved.features || {}) },
    };
  });
}

export function enabledAccessRoleKeys(
  roles?: OrgAccessRoleDefinition[] | null
): string[] {
  return normalizeAccessRoles(roles)
    .filter((r) => r.enabled)
    .map((r) => r.key);
}

export function roleAllowsFeature(
  roles: OrgAccessRoleDefinition[] | null | undefined,
  roleKey: string,
  feature: keyof OrgRoleFeatureFlags
): boolean {
  const role = normalizeAccessRoles(roles).find(
    (r) => r.key.toLowerCase() === (roleKey || "").toLowerCase()
  );
  if (!role || !role.enabled) return false;
  return !!role.features?.[feature];
}
